// Последний использованный способ входа — подсказка на экране входа
// («В прошлый раз вы входили через …»).
//
// Клиентское поле, БЕЗ серверного плагина: наш authClient — ручной fetch-враппер,
// а Capacitor всё равно не видит cookie API-домена (план §2.2, §5).
//
// Хранение — как у auth-storage.ts: localStorage (быстрое синхронное чтение)
// + зеркало в Capacitor Preferences (переживает очистку данных WebView).

import { IS_CAPACITOR } from "./platform-detect";

const STORAGE_KEY = "studyhub.lastLoginMethod";

// Способ входа остался ровно один — единый профиль экосистемы. Union держим
// массивом, а не литералом: он же служит белым списком при чтении хранилища.
export const LOGIN_METHODS = ["sso-exchange"] as const;
export type LoginMethod = (typeof LOGIN_METHODS)[number];

interface PreferencesApi {
	get: (opts: { key: string }) => Promise<{ value: string | null }>;
	set: (opts: { key: string; value: string }) => Promise<void>;
}

/** Specifier через переменную — иначе Vite import-analysis падает в web-сборке. */
async function loadPreferences(): Promise<PreferencesApi | null> {
	if (!IS_CAPACITOR || typeof window === "undefined") return null;
	const moduleId = "@capacitor/preferences";
	try {
		const mod = (await import(moduleId)) as { Preferences: PreferencesApi };
		return mod.Preferences;
	} catch {
		return null;
	}
}

function isLoginMethod(value: string | null): value is LoginMethod {
	return value !== null && (LOGIN_METHODS as readonly string[]).includes(value);
}

/**
 * У людей, входивших до переезда на IEC ID, в хранилище лежит `google` или
 * `email` — способов, которых больше нет. Значение вне белого списка не просто
 * игнорируем, а СТИРАЕМ: иначе оно переживает все будущие входы и в любой
 * момент может дойти до кода, который считает союз исчерпывающим.
 */
export function getLastLoginMethod(): LoginMethod | null {
	if (typeof window === "undefined") return null;
	try {
		const raw = window.localStorage.getItem(STORAGE_KEY);
		if (isLoginMethod(raw)) return raw;
		if (raw !== null) window.localStorage.removeItem(STORAGE_KEY);
		return null;
	} catch {
		return null;
	}
}

export function setLastLoginMethod(method: LoginMethod): void {
	if (typeof window === "undefined") return;
	try {
		window.localStorage.setItem(STORAGE_KEY, method);
	} catch {
		// приватный режим — подсказка просто не сохранится
	}
	void loadPreferences().then((prefs) => prefs?.set({ key: STORAGE_KEY, value: method }));
}

/**
 * Восстановить значение из Preferences, если хранилище WebView пусто.
 * Вызывается на старте мобильного приложения рядом с гидратацией токена.
 */
export async function hydrateLastLoginMethodFromNativeStorage(): Promise<void> {
	if (!IS_CAPACITOR || getLastLoginMethod()) return;
	const prefs = await loadPreferences();
	if (!prefs) return;
	try {
		const { value } = await prefs.get({ key: STORAGE_KEY });
		if (isLoginMethod(value)) window.localStorage.setItem(STORAGE_KEY, value);
	} catch {
		// Preferences недоступны — подсказку не покажем, вход не ломается
	}
}
